import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Header from 'src/components/Headers/Header';
import VerificationHeader from 'src/components/Headers/VerificationHeader';
import VerificationIconImg from 'src/assets/icons/VerificationIcon.png';
import { FC, useState, useEffect, useRef } from 'react';
import { forgotPassword } from 'src/services/auth.service';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import TextField from 'src/components/InputFields/TextField';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { routeURL, RESEND_EXPIRATION_TIME } from 'src/utils/properties.utils';
import {
  AuthFormContainer,
  AuthTitle,
  AuthFieldsContainer,
  AuthButton,
  InstructionText,
  AuthLinkContainer,
  CenteredButtonContainer,
  ResendButton,
  VerificationImage,
  VerificationPageContainer,
  VerificationTimerText,
} from 'src/components/StyledComponents/StyledAuthPages';
import { ButtonStyled } from 'src/components/StyledComponents/StyledPageComponents';

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

export const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const ForgotPw: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSnackbar } = StyledSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [remainingTime, setRemainingTime] = useState(RESEND_EXPIRATION_TIME);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const startTimer = () => {
    setRemainingTime(RESEND_EXPIRATION_TIME);
    setIsResendDisabled(true);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const response = await forgotPassword(values.email.trim());
        showSnackbar(response.data.message, 'success');
        setSentEmail(values.email.trim());
        setEmailSent(true);
        startTimer();
      } catch (e) {
        const errorMessage = await getApiErrorMessage(e);
        showSnackbar(errorMessage, 'error');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleResend = async () => {
    setIsResendDisabled(true);
    try {
      const response = await forgotPassword(sentEmail);
      showSnackbar(response.data.message, 'success');
      startTimer();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
      setIsResendDisabled(false);
    }
  };

  if (emailSent) {
    return (
      <VerificationPageContainer>
        <Header smScreen={smScreen} />

        <AuthFormContainer smScreen={smScreen} height={500} component="div">
          <VerificationHeader
            title="Check Your Email"
            subtitle={`We sent a password reset link to ${sentEmail}`}
          />

          {remainingTime > 0 && (
            <VerificationTimerText>
              {formatTime(remainingTime)}
            </VerificationTimerText>
          )}

          <VerificationImage src={VerificationIconImg} alt="Check Email" />

          <CenteredButtonContainer>
            <ResendButton
              onClick={handleResend}
              className={isResendDisabled ? 'btn-inactive' : 'btn-primary'}
              width="160px"
              disabled={isResendDisabled}
            >
              Resend Email
            </ResendButton>
          </CenteredButtonContainer>
        </AuthFormContainer>
      </VerificationPageContainer>
    );
  }

  return (
    <VerificationPageContainer>
      <Header smScreen={smScreen} />

      <AuthFormContainer
        smScreen={smScreen}
        height={380}
        component="form"
        onSubmit={formik.handleSubmit as any}
      >
        <AuthTitle smScreen={smScreen}>Forgot Your Password?</AuthTitle>

        <AuthFieldsContainer smScreen={smScreen}>
          <InstructionText>
            Please enter the email you use to sign in
          </InstructionText>

          <TextField
            id="email"
            label="Email"
            type="email"
            formikProps={formik}
          />
        </AuthFieldsContainer>

        <AuthButton smScreen={smScreen}>
          <ButtonStyled fullWidth type="submit" disabled={isSubmitting}>
            Submit
          </ButtonStyled>
        </AuthButton>

        <AuthLinkContainer smScreen={smScreen}>
          <Typography
            fontSize={{ xs: '12px', sm: '14px' }}
            fontFamily="Outfit"
            fontWeight="300"
            color="#0B2330"
          >
            <Link
              href={routeURL.LOGIN}
              underline="hover"
              color="var(--subtext)"
              fontSize={{ xs: '12px', sm: '14px' }}
              fontFamily="Lufga"
              fontWeight="500"
            >
              Back to Sign In
            </Link>
          </Typography>
        </AuthLinkContainer>
      </AuthFormContainer>
    </VerificationPageContainer>
  );
};

export default ForgotPw;
